import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Modal,
  Spin,
  Table,
  message,
  Select,
  Tooltip,
  Grid,
  Typography,
  Divider,
  Tabs,
  Collapse,
  Tag,
} from "antd";
import AppLayout from "../layout/AppLayout";
import { fetchMarketplaceList } from "../services/accessAPI";
import { useNavigate } from "react-router-dom";
import {
  FileTextOutlined,
  ApiOutlined,
  ApartmentOutlined,
  CodeOutlined,
  ShareAltOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";

const { Search } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;
const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const SolverMarketplace = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [filterCategory, setFilterCategory] = useState("description");
  const [InterfaceFrom, setInterfaceFrom] = useState([]);
  const [InterfaceTo, setInterfaceTo] = useState([]);
  const [realWorldProblem, setRealWorldProblem] = useState([]);
  const [category, setCategory] = useState("");
  const [uniquecategory, setUniqueCategory] = useState([]);
  const [uniqueInterfaceToOptions, setUniqueInterfaceToOptions] = useState([]);
  const [uniqueInterfaceFromOptions, setUniqueInterfaceFromOptions] = useState([]);
  const [uniquerealWorldProblem, setuniquerealWorldProblem] = useState([]);
  const [combinedInterfaceOptions, setCombinedInterfaceOptions] = useState([]);
  const [isModalVisibleEndpoint, setIsModalVisibleEndpoint] = useState(false);
  const [selectedEndpointIndex, setSelectedEndpointIndex] = useState(0);
  const [endpointOptions, setEndpointOptions] = useState([]);

  const normalizeValue = (value) => {
    if (!value || typeof value !== "string") return "";
    return value
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .trim()
      .toLowerCase()
      .replace(/^[a-z]/, (firstChar) => firstChar.toUpperCase());
  };

  // Load API data
  useEffect(() => {
    const loadApiConfig = async () => {
      try {
        const apiList = await fetchMarketplaceList();
        if (apiList) {
          setData(apiList);
        } else {
          message.warning("No data available.");
        }
      } catch (error) {
        console.error("Error fetching API list:", error);
        message.error("Failed to load marketplace data.");
      } finally {
        setLoading(false);
      }
    };
    loadApiConfig();
  }, []);

  const normalizeAndDeduplicate = (values) => {
    const uniqueValues = new Set();

    values.forEach((value, index) => {
      if (value && typeof value === "string") {
        // Remove all non-printable characters, normalize spaces, trim, and uppercase
        const normalized = value
          .replace(/[\u200B-\u200D\uFEFF]/g, "")
          .trim()
          .toLowerCase()
          .replace(/^[a-z]/, (firstChar) => firstChar.toUpperCase());

        if (normalized) {
          uniqueValues.add(normalized);
        }
      }
    });

    const result = [...uniqueValues].sort();
    return result;
  };

  // Replace your existing useEffect with this:
  useEffect(() => {
    const categoryValues = [];
    const interfaceToValues = [];
    const interfaceFromValues = [];
    const realWorldProblemValues = [];
    const combinedValues = [];

    data.forEach((item) => {
      // Collect category values
      if (item.category) {
        categoryValues.push(item.category);
      }

      // Process solvingProcesses array if it exists
      if (item.solvingProcesses && Array.isArray(item.solvingProcesses)) {
        item.solvingProcesses.forEach((process) => {
          // Process intermodalInterfaces for interfaceTo and interfaceFrom
          if (process.intermodalInterfaces && Array.isArray(process.intermodalInterfaces)) {
            process.intermodalInterfaces.forEach((interfaceObj) => {
              if (interfaceObj.interfaceTo) {
                interfaceToValues.push(interfaceObj.interfaceTo);
              }
              if (interfaceObj.interfaceFrom) {
                interfaceFromValues.push(interfaceObj.interfaceFrom);
              }

              // Create combined interface string "interfaceFrom -> interfaceTo"
              if (interfaceObj.interfaceFrom && interfaceObj.interfaceTo) {
                combinedValues.push(`${interfaceObj.interfaceFrom} -> ${interfaceObj.interfaceTo}`);
              }
            });
          }

          // Process realWorldProblems
          if (process.realWorldProblems && Array.isArray(process.realWorldProblems)) {
            process.realWorldProblems.forEach((problemObj) => {
              if (problemObj.realWorldProblem) {
                realWorldProblemValues.push(problemObj.realWorldProblem);
              }
            });
          }
        });
      }
    });

    // Normalize and deduplicate all collected values
    setUniqueCategory(normalizeAndDeduplicate(categoryValues));
    setUniqueInterfaceToOptions(normalizeAndDeduplicate(interfaceToValues));
    setUniqueInterfaceFromOptions(normalizeAndDeduplicate(interfaceFromValues));
    setuniquerealWorldProblem(normalizeAndDeduplicate(realWorldProblemValues));
    setCombinedInterfaceOptions(normalizeAndDeduplicate(combinedValues));
  }, [data]);

  // Open Modal and prepare interface data
  const openModal = (item) => {
    const interfaceData = (item.solvingProcesses || []).flatMap((process, processIdx) =>
      (process.intermodalInterfaces || []).map((interfaceObj, interfaceIdx) => ({
        key: `${process.id || processIdx}-${interfaceObj.id || interfaceIdx}`,
        interfaceFrom: interfaceObj.interfaceFrom,
        interfaceTo: interfaceObj.interfaceTo,
      }))
    );

    setSelectedItem(item);
    setModalData(interfaceData);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setModalData(null);
  };

  // Filter data based on selected category and search term
  const filteredData = data.filter((item) => {
    // Normalize the item's category
    const normalizedItemCategory = normalizeValue(item.category);
    // Normalize the selected category
    const normalizedSelectedCategory = normalizeValue(category);

    if (filterCategory === "interfaceTo") {
      const normalizedInterfaceTo = normalizeValue(InterfaceTo);
      return item.solvingProcesses?.some((process) =>
        process.intermodalInterfaces?.some(
          (interfaceObj) => normalizeValue(interfaceObj.interfaceTo) === normalizedInterfaceTo
        )
      );
    } else if (filterCategory === "interfaceFrom") {
      const normalizedInterfaceFrom = normalizeValue(InterfaceFrom);
      return item.solvingProcesses?.some((process) =>
        process.intermodalInterfaces?.some(
          (interfaceObj) => normalizeValue(interfaceObj.interfaceFrom) === normalizedInterfaceFrom
        )
      );
    } else if (filterCategory === "interfaceFromANDinterfaceTo") {
      const normalizedSearchTerm = normalizeValue(searchTerm);
      return item.solvingProcesses?.some((process) =>
        process.intermodalInterfaces?.some((interfaceObj) => {
          const normalizedFrom = normalizeValue(interfaceObj.interfaceFrom);
          const normalizedTo = normalizeValue(interfaceObj.interfaceTo);
          const combined = `${normalizedFrom} -> ${normalizedTo}`;

          return combined.toUpperCase() === normalizedSearchTerm.toUpperCase();
        })
      );
    } else if (filterCategory === "description") {
      const normalizedDescription = item.description.toLowerCase();
      const normalizedCategoryDescription = item.categoryDescription.toLowerCase();
      const normalizedSolvingProcesses = item.solvingProcesses?.[0]?.name?.toLowerCase() || "";
      const normalizedCategory = item.category.toLowerCase();
      const normalizedSearchTerm = searchTerm.toLowerCase();
      const matchesDescription = normalizedDescription.includes(normalizedSearchTerm);
      const matchesCategoryDescription = normalizedCategoryDescription.includes(normalizedSearchTerm);
      const matchesSolvingProcesses = normalizedSolvingProcesses.includes(normalizedSearchTerm);
      const matchesCategory = normalizedCategory.includes(normalizedSearchTerm);

      return matchesDescription || matchesCategoryDescription || matchesSolvingProcesses || matchesCategory;
    } else if (filterCategory === "realWorldProblem") {
      const normalizedRealWorldProblem = normalizeValue(realWorldProblem);
      return item.solvingProcesses?.some((process) =>
        process.realWorldProblems?.some((obj) => normalizeValue(obj.realWorldProblem) === normalizedRealWorldProblem)
      );
    } else if (filterCategory === "category") {
      return normalizedItemCategory === normalizedSelectedCategory;
    }
    return false;
  });

  const handleSelectChange = (value) => {
    setFilterCategory(value);
    // Clear previous filter values
    setSearchTerm("");
    setInterfaceFrom([]);
    setInterfaceTo([]);
    setRealWorldProblem([]);
    setCategory([]);
  };

  const runSolversClick = (item) => {
    const descriptions = item.endpoints.map((endpoint) => ({
      method: endpoint.method,
      path: endpoint.path,
      description: endpoint.description,
      basePath: item.basePath,
      expectedInput: endpoint.expectedInput ?? [],
      name: item.name,
      id: item.id,
      expectedOutput: endpoint.expectedOutput ?? [],
    }));

    if (descriptions.length === 1) {
      const expectedInput = descriptions[0]?.expectedInput;
      const hasExpectedInput = Array.isArray(expectedInput) && expectedInput.length > 0;
      const formatValue = hasExpectedInput ? expectedInput[0]?.format : null;
      const UrlIframe = descriptions[0].basePath;
      const isIframe = formatValue === "iframe";
      if (isIframe) {
        navigate("/SolverIframe", { state: { url: UrlIframe } });
      } else {
        navigate("/Results", {
          state: { description: descriptions[0], pageToNavigate: "Solver_Marketplace" },
        });
      }
    } else {
      // More than one endpoint — show modal
      setSelectedItem(item);
      setEndpointOptions(descriptions);
      setIsModalVisibleEndpoint(true);
    }
  };

  const handleOkEndpoint = () => {
    const selectedDescription = endpointOptions[selectedEndpointIndex];
    setIsModalVisibleEndpoint(false);
    navigate("/Results", {
      state: {
        description: selectedDescription,
        pageToNavigate: "Solver_Marketplace",
      },
    });
  };

  const handleCancelEndpoint = () => {
    setIsModalVisibleEndpoint(false);
  };

  // Use dynamic styles for mobile responsiveness
  const selectStyle = {
    width: screens.xs ? "100%" : "30%",
    marginBottom: 10,
    marginRight: screens.xs ? 0 : "2%",
  };

  const searchStyle = {
    width: screens.xs ? "100%" : "30%",
  };

  return (
    <AppLayout>
      <h1 style={{ marginBottom: 20, color: "#142745" }}>Solver Marketplace</h1>
      <p>Please select your preferred search option.</p>
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Select style={selectStyle} value={filterCategory} onChange={handleSelectChange}>
          <Option value="description">All Solvers</Option>
          <Option value="interfaceFrom">Interface From</Option>
          <Option value="interfaceTo">Interface To</Option>
          <Option value="interfaceFromANDinterfaceTo">{" Interface From -> Interface To"}</Option>
          <Option value="realWorldProblem">Real World Problem</Option>
          <Option value="category">Category</Option>
        </Select>

        {filterCategory === "interfaceFromANDinterfaceTo" && (
          <Select
            style={selectStyle}
            onChange={(value) => setSearchTerm(value)}
            placeholder="Select Interface From -> Interface To"
          >
            {combinedInterfaceOptions.map((combined, idx) => (
              <Option key={idx} value={combined}>
                {combined}
              </Option>
            ))}
          </Select>
        )}

        {filterCategory === "realWorldProblem" && (
          <Select
            style={selectStyle}
            value={realWorldProblem}
            onChange={(value) => setRealWorldProblem(value)}
            placeholder="Select Real World Problem"
          >
            {uniquerealWorldProblem.map((problem, idx) => (
              <Option key={idx} value={problem}>
                {problem}
              </Option>
            ))}
          </Select>
        )}

        {filterCategory === "interfaceTo" && (
          <Select
            style={selectStyle}
            value={InterfaceTo}
            onChange={(value) => setInterfaceTo(value)}
            placeholder="Select Interface To"
          >
            {uniqueInterfaceToOptions.map((interfaceTo, idx) => (
              <Option key={idx} value={interfaceTo}>
                {interfaceTo}
              </Option>
            ))}
          </Select>
        )}

        {filterCategory === "interfaceFrom" && (
          <Select
            style={selectStyle}
            value={InterfaceFrom}
            onChange={(value) => setInterfaceFrom(value)}
            placeholder="Select Interface From"
          >
            {uniqueInterfaceFromOptions.map((interfaceFrom, idx) => (
              <Option key={idx} value={interfaceFrom}>
                {interfaceFrom}
              </Option>
            ))}
          </Select>
        )}

        {filterCategory === "category" && (
          <Select
            style={selectStyle}
            value={category}
            onChange={(value) => setCategory(value)}
            placeholder="Select Category"
          >
            {uniquecategory.map((category, idx) => (
              <Option key={idx} value={category}>
                {category}
              </Option>
            ))}
          </Select>
        )}

        {filterCategory === "description" && (
          <Search
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchStyle}
            allowClear
            className="custom-search"
          />
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", margin: "50px 0" }}>
          <Spin tip="Loading...">
            <div style={{ height: "100px" }} />
          </Spin>
        </div>
      ) : (
        <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "10px" }}>
          <Row gutter={[16, 16]}>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <Col key={item.id || `default-${index}`} xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <Card
                    title={
                      <Tooltip title={item.description}>
                        <span
                          style={{
                            display: "block",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {item.description}
                        </span>
                      </Tooltip>
                    }
                    bordered
                    style={{
                      height: "100%", // Makes sure the card takes full height
                      padding: 20,
                      borderRadius: 10,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      backgroundColor: "#ffffff",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    styles={{
                      body: {
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      },
                    }}
                  >
                    {/* Content Section */}
                    <div style={{ flex: 1, overflowY: "auto" }}>
                      <section>
                        <h6 className="text-lg font-semibold">Description of Solver:</h6>
                        {item.endpoints.map((endpoint, index) => (
                          <div key={index}>
                            <p className="text-gray-700">{endpoint.description}</p>
                          </div>
                        ))}
                      </section>
                      <p>
                        <strong>ID:</strong> {item.name}
                      </p>
                      <p>
                        {item.solvingProcesses?.[0]?.name && (
                          <p>
                            <strong>Related Solving Process:</strong> {item.solvingProcesses[0].name}
                          </p>
                        )}
                      </p>
                      <p>
                        <strong>Category:</strong> {item.category}
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "10px", // space between buttons
                        marginTop: "auto",
                        paddingTop: "10px",
                      }}
                    >
                      <Button type="primary" onClick={() => openModal(item)}>
                        Read More
                      </Button>
                      <Button onClick={() => runSolversClick(item)}>Run</Button>
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <p>No results found.</p>
              </Col>
            )}
          </Row>
        </div>
      )}

      {isModalOpen && selectedItem && modalData && (
        <MarketplaceModal
          item={selectedItem}
          visible={isModalOpen}
          onClose={closeModal}
          interfaceData={modalData}
          screens={screens}
        />
      )}

      <Modal
        title="Please select option"
        open={isModalVisibleEndpoint}
        onOk={handleOkEndpoint}
        onCancel={handleCancelEndpoint}
        okText="Continue"
      >
        <Select
          style={{ width: "100%", height: "50%" }}
          value={selectedEndpointIndex}
          onChange={(value) => setSelectedEndpointIndex(value)}
        >
          {endpointOptions.map((endpoint, index) => (
            <Option key={index} value={index}>
              <strong>{endpoint.method}</strong> {endpoint.path} — {endpoint.description}
            </Option>
          ))}
        </Select>
      </Modal>
    </AppLayout>
  );
};

const MarketplaceModal = ({ item, visible, onClose, interfaceData, screens }) => (
  <Modal
    title={item.description}
    open={visible}
    onCancel={onClose}
    width={900}
    footer={[
      <Button key="close" onClick={onClose}>
        Close
      </Button>,
    ]}
  >
    <Tabs defaultActiveKey="1" type="card">
      {/* 1. Description */}
      <TabPane
        tab={
          <span>
            <FileTextOutlined />
            Description
          </span>
        }
        key="1"
      >
        <Title level={4}>Description of Solver</Title>
        <section>
          {item.endpoints.map((endpoint, index) => (
            <div key={index}>
              <p className="text-gray-700">{endpoint.description}</p>
            </div>
          ))}
        </section>

        <Divider />
        <Title level={5}>ID of Solver</Title>
        <Paragraph>{item.name}</Paragraph>
      </TabPane>

      {/* 3. Real-world Problems */}
      <TabPane
        tab={
          <span>
            <ApartmentOutlined />
            Real-world Problems
          </span>
        }
        key="2"
      >
        <Title level={4}>Real-world Problems</Title>
        <ul style={{ paddingLeft: "20px" }}>
          {item.solvingProcesses?.[0]?.realWorldProblems?.map((p, i) => (
            <li key={i}>{p.realWorldProblem}</li>
          ))}
        </ul>
      </TabPane>

      {/* 4. Category */}
      <TabPane
        tab={
          <span>
            <AppstoreOutlined />
            Category
          </span>
        }
        key="3"
      >
        <Title level={4}>{item.category}</Title>
        <Paragraph>{item.categoryDescription}</Paragraph>
      </TabPane>

      {/* 5. Solving Process */}
      <TabPane
        tab={
          <span>
            <ShareAltOutlined />
            Solving Process
          </span>
        }
        key="4"
      >
        {item.solvingProcesses?.map((sp, i) => (
          <Paragraph key={i}>
            {sp.name && <Text strong>Name:</Text>} {sp.name || ''}
          </Paragraph>
        ))}
      </TabPane>

      {/* 6,7,8 Endpoints */}
      <TabPane
        tab={
          <span>
            <ApiOutlined />
            Endpoints
          </span>
        }
        key="5"
      >
        <Collapse accordion>
          {item.endpoints.map((ep, idx) => (
            <Panel
              header={
                <span>
                  <Tag color={ep.method === "GET" ? "blue" : "green"}>{ep.method}</Tag> {ep.path} - {ep.description}
                </span>
              }
              key={idx}
            >
              <Paragraph>
                <Text strong>Description:</Text> {ep.description}
              </Paragraph>

              <Divider />

              <Title level={5}>Expected Input</Title>
              {ep.expectedInput?.length > 0 ? (
                <ul style={{ paddingLeft: "20px" }}>
                  {ep.expectedInput.map((input, i) => (
                    <li key={i}>
                      <Text>
                        <strong>Name:</strong> {input.name ?? "null"} | <strong>Format:</strong>{" "}
                        {input.format ?? "null"} | <strong>Location:</strong> {input.in ?? "null"} |{" "}
                        <strong>Unit:</strong> {input.unit ?? "null"} | <strong>Label:</strong> {input.label ?? "null"}
                      </Text>
                    </li>
                  ))}
                </ul>
              ) : (
                <Text type="secondary">Null</Text>
              )}

              <Divider />

              <Title level={5}>Expected Output</Title>
              {ep.expectedOutput?.length > 0 ? (
                <ul style={{ paddingLeft: "20px" }}>
                  {ep.expectedOutput.map((output, i) => (
                    <li key={i}>
                      <Text>
                        <strong>Name:</strong> {output.name ?? "null"} | <strong>Format:</strong>{" "}
                        {output.format ?? "null"} | <strong>Location:</strong> {output.in ?? "null"} |{" "}
                        <strong>Unit:</strong> {output.unit ?? "null"} | <strong>Label:</strong>{" "}
                        {output.label ?? "null"}
                      </Text>
                    </li>
                  ))}
                </ul>
              ) : (
                <Text type="secondary">Null</Text>
              )}
            </Panel>
          ))}
        </Collapse>

        <Divider />
        <Title level={5}>Base Path</Title>
        <Paragraph>{item.basePath}</Paragraph>
      </TabPane>

      {/* 9. Intermodal Interfaces */}
      <TabPane
        tab={
          <span>
            <CodeOutlined />
            Interfaces
          </span>
        }
        key="6"
      >
        <Title level={4}>Intermodal Interfaces</Title>
        <Table
          columns={[
            { title: "Interface From", dataIndex: "interfaceFrom", key: "interfaceFrom" },
            { title: "Interface To", dataIndex: "interfaceTo", key: "interfaceTo" },
          ]}
          dataSource={interfaceData}
          pagination={interfaceData.length > 3 ? { pageSize: 3 } : false}
          rowKey="key"
          size="small"
        />
      </TabPane>
    </Tabs>
  </Modal>
);

export default SolverMarketplace;
